import React, { useState } from 'react';
import { Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import SortableList from '@shared/components/SortableList';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useSortableItemContext } from '@shared/components/SortableList/context/SortableItem.context';
import { pxToRem } from '@withjoy/joykit/theme';
import { Drag } from '@withjoy/joykit/icons';
import { ScoringPriorityField } from '@graphql/generated';
import { useEventVenueDialogTelemetry } from '../EventVenueDialog/EventVenueDialog.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';

interface FiltersOrderProps {
  handleOnChangeOrder: (scoringPriorityFields: ReadonlyArray<ScoringPriorityField>) => void;
}

export const FiltersOrder = ({ handleOnChangeOrder }: FiltersOrderProps) => {
  const { t } = useTranslation('websiteEditor');
  const venueFiltersTrans = t('venueFilters');
  const { eventHandle } = useEventInfo();
  const telemetry = useEventVenueDialogTelemetry();

  const defaultScoringPriorityFields: ReadonlyArray<ScoringPriorityFieldData> = [
    {
      id: '1',
      isDragActive: false,
      title: venueFiltersTrans.standardRoomPriceDiscountPercent.title(),
      tooltip: venueFiltersTrans.standardRoomPriceDiscountPercent.tooltip(),
      value: ScoringPriorityField.standardRoomPriceDiscountPercent,
    },
    {
      id: '2',
      isDragActive: false,
      title: venueFiltersTrans.userRating.title(),
      tooltip: venueFiltersTrans.userRating.tooltip(),
      value: ScoringPriorityField.userRating,
    },
    {
      id: '3',
      isDragActive: false,
      title: venueFiltersTrans.distanceFromSearchLocation.title(),
      tooltip: venueFiltersTrans.distanceFromSearchLocation.tooltip(),
      value: ScoringPriorityField.distanceFromSearchLocation,
    },
    {
      id: '4',
      isDragActive: false,
      title: venueFiltersTrans.couplesPicks.title(),
      tooltip: venueFiltersTrans.couplesPicks.tooltip(),
      value: ScoringPriorityField.standardRoomPriceDiscountPercent, // @todo - TBD - For now used dummy text
    },
  ];
  const [scoringPriorityFieldsData, setScoringPriorityFieldsData] = useState(defaultScoringPriorityFields);

  const [isDragActive, setIsDragActive] = useState(false);

  const handleDragStart = useEventCallback(() => {
    setIsDragActive(true);
  });

  const handleDragCancel = useEventCallback(() => {
    setIsDragActive(false);
  });

  const handleDragEnd = useEventCallback(() => {
    setIsDragActive(false);
  });

  const onChangeOrder = useEventCallback((items: ReadonlyArray<ScoringPriorityFieldData>) => {
    setScoringPriorityFieldsData(items);
    const scoringPriorityFieldsWithDummyField = items.map(item => item.title);
    telemetry.scoringPriorityFieldsChanged(scoringPriorityFieldsWithDummyField, eventHandle);
    const updatedScoringPriorityFields = items.filter(item => item.title !== venueFiltersTrans.couplesPicks.title()).map(item => item.value);
    handleOnChangeOrder(updatedScoringPriorityFields);
  });

  return (
    <Flex
      flexDirection="column"
      gap={5}
      alignItems="flex-start"
      justifyContent="center"
      paddingX={{ _: 6, md: 0 }}
    >
      <SortableList
        customStyle={{ width: '100%' }}
        items={[...scoringPriorityFieldsData]}
        renderItem={(scoringPriorityFieldData, idx) => {
          return (
            <ScoringPriorityFieldItem
              key={`sortable-filters-order-${scoringPriorityFieldData.id}`}
              {...scoringPriorityFieldData}
              isDragActive={isDragActive}
              id={`${idx + 1}`}
            />
          );
        }}
        onChange={onChangeOrder}
        hasDragHandle={true}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        handleDragCancel={handleDragCancel}
      />
    </Flex>
  );
};

interface ScoringPriorityFieldData {
  id: string;
  isDragActive: boolean;
  title: string;
  tooltip: string;
  value: ScoringPriorityField;
}

export const ScoringPriorityFieldItem = ({ id, title, tooltip, isDragActive }: ScoringPriorityFieldData) => {
  const { attributes, listeners, draggableNodeRef } = useSortableItemContext();
  return (
    <Flex
      alignItems={'center'}
      gap={3}
      width={'100%'}
    >
      <Flex
        alignItems={'center'}
        cursor={isDragActive ? 'grabbing' : 'grab'}
        {...attributes}
        {...listeners}
        ref={draggableNodeRef}
      >
        <Drag size={24} />
      </Flex>
      <Flex
        gap={5}
        paddingX={4}
        paddingY={3}
        border={'1px solid'}
        borderColor={'mono3'}
        borderRadius={pxToRem(200)}
        alignItems={'center'}
        flex={1}
      >
        <TextV2
          typographyVariant={'hed1'}
          fontSize={pxToRem(16)}
          paddingX={pxToRem(3)}
          paddingY={pxToRem(10)}
          borderRadius={'50%'}
          backgroundColor={'mono3'}
          width={pxToRem(40)}
          height={pxToRem(40)}
          textAlign={'center'}
          flex={'none'}
        >
          {id}
        </TextV2>
        <TooltipV2
          overrides={{
            Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white', marginLeft: 2 } },
          }}
          content={
            <Flex
              typographyVariant="label2"
              color="mono4"
              maxWidth={pxToRem(320)}
            >
              {tooltip}
            </Flex>
          }
          placement="top"
          onMouseEnterDelay={500}
        >
          <TextV2
            typographyVariant={'hed1'}
            cursor={'default'}
          >
            {title}
          </TextV2>
        </TooltipV2>
      </Flex>
    </Flex>
  );
};
